/**
 * 身份字段 —— 明文、独立带（2026-08-07 决策）。
 *
 * 规范：`docs/engineering/tracking/event-data-dictionary.md` §3.1 / §3.2。
 * 新增一个标识（如 `phone`）只需在 [IDENTITY_FIELDS] 加一行，其余不用动。
 */

/** 身份来源对象；字段缺失即不写该 metadata。 */
export interface TrackProfile {
  ownerId?: string | number | null;
  email?: string | null;
  twitterHandle?: string | null;
  /** 登录方式：twitter / google / apple … */
  loginChannel?: string | null;
  /** 预留，接入时无需改本文件之外的代码。 */
  phone?: string | null;
}

/**
 * metadata key → 取值器。**加新标识就在这里加一行。**
 * key 命名 snake_case，与 native 侧 `analytics/Identity.kt` 的 `IDENTITY_FIELDS` 逐字对齐。
 */
export const IDENTITY_FIELDS: Record<
  string,
  (p: TrackProfile) => string | null | undefined
> = {
  owner_id: (p) => (p.ownerId == null ? null : String(p.ownerId)),
  email: (p) => p.email,
  twitter_handle: (p) => p.twitterHandle,
  login_channel: (p) => p.loginChannel,
  phone: (p) => p.phone,
};

export type MetadataWriter = (key: string, value: string) => void;

/**
 * 登录后写身份 metadata。空值跳过（不写空串，避免把"没有"写成"有但为空"）。
 * @returns 实际写入的 key 列表（供单测断言）
 */
export function applyIdentity(
  profile: TrackProfile,
  write: MetadataWriter,
): string[] {
  const written: string[] = [];
  for (const [key, pick] of Object.entries(IDENTITY_FIELDS)) {
    const value = pick(profile);
    if (value == null || value === "") {
      continue;
    }
    write(key, String(value));
    written.push(key);
  }
  return written;
}

/**
 * 登出清身份 metadata。
 *
 * **OpenReplay 的 metadata 没有 delete，只能覆盖成空串** —— 因此这里必须把
 * [IDENTITY_FIELDS] 的**每一个** key 都写一次 `""`，不能只清"这次写过的"。
 * 漏清会导致同一设备换账号登录时，上一个用户的邮箱串到新 session 上。
 *
 * @returns 被清空的 key 列表（供单测断言与 [applyIdentity] 对称）
 */
export function clearIdentity(write: MetadataWriter): string[] {
  const cleared: string[] = [];
  for (const key of Object.keys(IDENTITY_FIELDS)) {
    write(key, "");
    cleared.push(key);
  }
  return cleared;
}
